import { Component, Input, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { FormErrorComponent } from '../form-error/form-error.component';

/**
 * IsCurrencyFieldComponent
 *
 * Campo monetário com formatação automática.
 * Armazena o valor numérico internamente e exibe formatado.
 *
 * @example
 * <is-currency-field
 *   label="Valor"
 *   prefix="R$"
 *   formControlName="amount"
 *   [control]="form.get('amount')"
 * ></is-currency-field>
 */
@Component({
  selector: 'is-currency-field',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormErrorComponent],
  templateUrl: './currency-field.component.html',
  styleUrls: ['./currency-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyFieldComponent),
      multi: true,
    },
  ],
})
export class CurrencyFieldComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() placeholder = '0,00';
  @Input() prefix = 'R$';
  @Input() required = false;
  @Input() helpText = '';
  @Input() control: AbstractControl | null = null;
  @Input() decimalSeparator = ',';
  @Input() thousandSeparator = '.';

  displayValue = '';
  isDisabled = false;

  private numericValue: number | null = null;
  private onChange: (val: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  get hasError(): boolean {
    return !!(this.control?.invalid && (this.control?.dirty || this.control?.touched));
  }

  writeValue(val: any): void {
    const numeric = (typeof val === 'number' && !isNaN(val)) ? val : null;
    this.numericValue = numeric;
    this.displayValue = numeric != null ? this.formatNumber(numeric) : '';
  }

  registerOnChange(fn: (val: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
  }

  handleInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    // Remove tudo que não é dígito
    const digits = raw.replace(/\D/g, '');
    const numeric = digits.length > 0 ? parseInt(digits, 10) / 100 : null;

    this.numericValue = numeric;
    this.displayValue = numeric != null ? this.formatNumber(numeric) : '';
    this.onChange(numeric);
  }

  handleBlur(): void {
    this.onTouched();
  }

  private formatNumber(value: number): string {
    const parts = value.toFixed(2).split('.');
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, this.thousandSeparator);
    return `${intPart}${this.decimalSeparator}${parts[1]}`;
  }
}
